var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _class, _class2, _descriptor;

function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }

function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'proposal-class-properties is enabled and runs after the decorators transform.'); }

function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }

/*!
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
import { Component, html, classMap, nothing, styleMap, Event, Prop } from '@sick-davinci/ce-decorators';
import { style } from './MultiSelect.style';
import { DropDown } from '../drop-down/DropDown';
export
/**
 * A `davinci-multi-select` is a specialization of the 'davinci-drop-down' to support multiple value selections.
 * Each entry has its own checkbox to display it's state.
 * If used as a combo-box (editable), the content of the text-field displays the input instead of the selections.
 *
 * @example
 * ```html
 * <davinci-multi-select>
 *   <davinci-option value="1">One</davinci-option>
 *   <davinci-option value="2">Two</davinci-option>
 *   <davinci-option value="3">Three</davinci-option>
 * </davinci-multi-select>
 *  ```
 */
let MultiSelect = (_dec = Component({
  tag: 'davinci-multi-select',
  style: [style],
  inheritStyle: true,
  dependencies: [DropDown]
}), _dec2 = Reflect.metadata("design:paramtypes", []), _dec3 = Prop({
  reflectAsAttribute: false
}), _dec4 = Reflect.metadata("design:type", Object), _dec5 = Event('change'), _dec6 = Reflect.metadata("design:type", Object), _dec(_class = _dec2(_class = (_class2 = class MultiSelect extends DropDown {
  /**
   * Get or set the value.
   */
  set values(value) {
    this._values = Array.isArray(value) ? value : [value];
  }

  get values() {
    return this.editable ? this.searchTerm || '' : this.selectedValues;
  }

  get selections() {
    if (this._values && this._values.length > 0 && this.internalDataValuesFlatted && this.internalDataValuesFlatted.length > 0) {
      const selections = this.internalDataValuesFlatted.filter(value => this._values.find(item => item === value.value));
      this.selectedValues = selections.map(item => item.value);
      return selections;
    } else {
      this.selectedValues = [];
      return [];
    }
  }

  set searchTerm(value) {
    this._searchTerm = value;
  }

  get searchTerm() {
    return this._searchTerm;
  }

  set selectedValues(value) {
    this._selectedValues = value;
  }

  get selectedValues() {
    return this._selectedValues;
  }
  /**
   * Prevent the default event and stop the event propagation
   * @param {Event} e The event that should be prevented
   */


  static preventEvent(e) {
    e.preventDefault();
    e.stopPropagation();
  }
  /**
   * Emitted if the value changes.
   */


  constructor() {
    super();

    _initializerDefineProperty(this, "changeEvent_", _descriptor, this);

    this._searchTerm = '';
    this._values = [];
    this._selectedValues = [];

    this.onInputInput = event => {
      MultiSelect.preventEvent(event);
      this.expanded = true;
      this.searchTerm = event.currentTarget.value;
      this.input.emit(this.searchTerm);
      this.scheduleRender(true);
    };

    this.onInputKeyDown = event => {
      if (event.key === 'Enter' && !this._selectionCursor) {
        MultiSelect.preventEvent(event);
        this.changeEvent_.emit(this._values);
      }
    };

    this.onClickCapture = event => {
      if (event.currentTarget.className.indexOf('overlaywrapper') >= 0) {
        MultiSelect.preventEvent(event);
      }

      if (event.target.tagName === 'INPUT') {
        this.expanded = true;
      } else {
        this.expanded = !this.expanded;
        this.resetSelectionCursor();
      }
    };

    this.onOptionClicked = event => {
      MultiSelect.preventEvent(event);

      this._selectMultiOption(event.currentTarget, true);

      this.changeEvent_.emit(this._values);
    };

    this.onOptionParentClicked = event => {
      MultiSelect.preventEvent(event);

      this._selectMultiOptionParent(event.currentTarget);

      this.changeEvent_.emit(this._values);
    };

    this.contentRenderer = this._customRenderer;
    this.renderOptionsList = this.renderOptionsList;
  }

  render() {
    return html`
      <div
        class="${classMap({
      icon: !!this.iconSrc,
      container: true,
      expanded: this.expanded,
      placeholder: !this.selections || this.selections.length < 1,
      focus: this.hasFocus || this.hasHover
    })}"
        @click=${this.onClickCapture}
      >
        ${this.renderLabel()}
        ${this.renderContainerContent()}
      </div>
      ${this.renderOverlayWrapper()}
    `;
  }

  renderLabel() {
    var _this$selections;

    const showLabel = this.placeholder || ((_this$selections = this.selections) === null || _this$selections === void 0 ? void 0 : _this$selections.length) > 0 || this.expanded || this.editable;
    return showLabel ? html`<label class="label">${this.label}</label>` : html``;
  }

  renderInput() {
    return html`
      <input
        class="input"
        title="${this._values ? this._values : ''}"
        type="text"
        .placeholder="${this.placeholder}"
        .values="${this._values ? this._values : ''}"
        @blur="${this.blurHandler}"
        @focus="${this.onInputFocus}"
        @input="${this.onInputInput}"
        @change="${this.changeHandler}"
        @keydown="${this.onInputKeyDown}"
      />
      <div class="currentvalue">${this._renderValueSlot()}</div>
      <davinci-spinner class="spinner"></davinci-spinner>
    `;
  }

  onInputChange(event) {
    MultiSelect.preventEvent(event);

    this._selectMultiOption(this._selectionCursor, true);
  }

  selectCursorSelectedOption(event) {
    // Skip if no cursor is set
    if (!this._selectionCursor) {
      return;
    }

    event.preventDefault();
    const option = this._selectionCursor;

    if (this._getChildrenContainer()) {
      this._selectMultiOptionParent(option);
    } else {
      this._selectMultiOption(option, true);
    }

    this.changeEvent_.emit(this._values);
  }

  renderOptionsList() {
    return html`
      <div
        class="${classMap({
      list: true,
      empty: !this.internalDataValuesFlatted || this.internalDataValuesFlatted.length === 0
    })}"
        style="${styleMap(this.listStyle)}"
        @click="${this.disabledOptionClickHandler}"
      >
        ${this.internalData && this.internalData.map(value => {
      if (typeof value === 'string') {
        return this._renderOptionFor(value);
      } else if ('data' in value) {
        return this._renderOptionParentAndChildrenFor(value);
      } else if (this.contentRenderer(value.value, value.label)) {
        // Take care, that empty strings don't get rendered, otherwise see [DAVIBE-277]
        return this._renderOptionFor(value);
      } else {
        return null;
      }
    })}
      </div>
    `;
  }

  extractDataFromLightDom(mutations, init = true) {
    if (mutations && !init && mutations.filter(value => value.target !== this || value.target === this && value.type === 'childList').length === 0) {
      // tslint:disable-line
      return;
    }

    if (init && this.children.length === 0) {
      return;
    }

    this.data = Array.from(this.children).filter(element => element.tagName.startsWith('DAVINCI-OPTION-GROUP') || element.tagName.startsWith('DAVINCI-OPTION') || element.tagName === 'OPTION' || element.tagName === 'OPTGROUP') // filter only davinci-option or davinci-option-group elements
    .map(optionOrOptionGroup => {
      if (optionOrOptionGroup.tagName.startsWith('DAVINCI-OPTION-GROUP') || optionOrOptionGroup.tagName === 'OPTGROUP') {
        return {
          data: Array.from(optionOrOptionGroup.children).filter(value => value.tagName.startsWith('DAVINCI-OPTION') || value.tagName === 'OPTION').map(option => this._buildMultiDropDownOptionFor(option)),
          label: optionOrOptionGroup.label
        };
      } else {
        return this._buildMultiDropDownOptionFor(optionOrOptionGroup);
      }
    });
  }

  moveSelectionCursor(direction) {
    direction === 'up' ? this._moveSectionCursorUp() : this._moveSectionCursorDown();
  }

  _buildMultiDropDownOptionFor(optionOrOptionGroup) {
    const {
      disabled,
      innerHTML,
      selected,
      value
    } = optionOrOptionGroup;
    return {
      disabled,
      label: innerHTML.trim() || value,
      selected,
      value: value !== null && value !== void 0 ? value : innerHTML.trim()
    };
  }

  _renderOptionFor(value, disabled) {
    // @todo: Syntax?
    // This seems like an ugly fix, but typescript needs something like this
    // to be satisfied :(. Please advice for better handling of such situations.
    // See line 295 for how this could be done easily without triggering typescript
    let props = {};

    if (typeof value === 'string') {
      props = {
        disabled,
        label: value,
        value
      };
    } else {
      props = value;
    } // const valueIsString = typeof value === 'string';
    // const props = {
    //   disabled: valueIsString ? disabled : value.disabled,
    //   label: valueIsString ? value : value.label,
    //   value: valueIsString ? value : value.value,
    // };


    return html`
      <davinci-option
        class="option"
        ?disabled="${props.disabled}"
        .value=${props.value}
        @click="${this.onOptionClicked}"
      >
        ${this.contentRenderer(props.value, props.label)}
        <davinci-checkbox
          ?checked="${this._isOptionChecked(props.value)}"
          ?disabled="${disabled}"
        ></davinci-checkbox>
      </davinci-option>
    `;
  }

  _renderOptionParentAndChildrenFor(value) {
    const children = value.data;

    if (!children.length) {
      return nothing;
    }

    const selected = this._areAllChildrenChecked(children);

    const disabled = !this._hasClickableChildren(children);
    return html`
      <davinci-option
        class="option-parent"
        ?disabled="${disabled}"
        ?selected="${selected}"
        @click="${this.onOptionParentClicked}"
      >
        <div class="option-parent-item">
          <span class="label">${value.label}</span>
          <davinci-checkbox
            ?checked="${selected}"
            ?disabled="${disabled}"
          ></davinci-checkbox>
        </div>
        ${this._renderOptionChildrenFor(children, disabled)}
      </davinci-option>
    `;
  }

  _renderOptionChildrenFor(data, disabled) {
    return html`
      <div class="children">
        ${data.map(groupValue => typeof groupValue === 'string' ? this._renderOptionFor(groupValue, disabled) : this._renderOptionGroupFor(groupValue, disabled))}
      </div>
    `;
  }

  _renderOptionGroupFor(groupValue, disabled) {
    return html`
      <davinci-option
        class="option"
        ?disabled="${disabled || groupValue.disabled}"
        .value=${groupValue.value}
        @click="${this.onOptionClicked}"
      >
        ${this.contentRenderer(groupValue.value, groupValue.label)}
        <davinci-checkbox
          ?checked="${this._isOptionChecked(groupValue.value)}"
          ?disabled="${disabled || groupValue.disabled}"
        ></davinci-checkbox>
      </davinci-option>
    `;
  }

  _moveSectionCursorUp() {
    const direction = 'up';

    if (!this._selectionCursor) {
      this._selectLastElementIn(this._getOptionsContainer().children, direction);

      if (this._hasChildren(this._selectionCursor)) {
        this._selectLastElementIn(this._getChildrenContainer().children, direction);
      }
    } else if (this._selectionCursor.previousElementSibling) {
      this.setSelectionCursorOn(this._selectionCursor.previousElementSibling, direction);

      if (this._hasChildren(this._selectionCursor)) {
        this._selectLastElementIn(this._getChildrenContainer().children, direction);
      }
    } else {
      const isNextElementSelected = this._selectNext(this._selectionCursor.previousElementSibling, direction);

      if (!isNextElementSelected) {
        this._selectNextParentElement(direction);
      }
    }
  }

  _selectLastElementIn(list, direction) {
    this.setSelectionCursorOn(list[list.length - 1], direction);
  }

  _moveSectionCursorDown() {
    const direction = 'down';

    if (!this._selectionCursor) {
      this._selectFirstElementIn(this._getOptionsContainer().children, direction);
    } else if (this._hasChildren(this._selectionCursor)) {
      this._selectFirstElementIn(this._getChildrenContainer().children);
    } else {
      const isNextElementSelected = this._selectNext(this._selectionCursor.nextElementSibling, direction);

      if (!isNextElementSelected) {
        this._selectNextParentElement(direction);
      }
    }
  }

  _selectFirstElementIn(list, direction) {
    this.setSelectionCursorOn(list[0], direction);
  } // Selects the next element and returns false, if still the same element is selected


  _selectNext(element, direction) {
    const selectionCursorBefore = this._selectionCursor;
    this.setSelectionCursorOn(element, direction);
    const selectionCursorAfter = this._selectionCursor;
    return selectionCursorBefore !== selectionCursorAfter;
  }

  _selectNextParentElement(direction) {
    if (direction === 'up') {
      this.setSelectionCursorOn(this._selectionCursor.parentElement.parentElement);
    } else {
      this.setSelectionCursorOn(this._selectionCursor.parentElement.parentElement.nextElementSibling);
    }
  }

  _renderValueSlot() {
    var _this$selections2;

    return ((_this$selections2 = this.selections) === null || _this$selections2 === void 0 ? void 0 : _this$selections2.length) > 0 ? this.contentRenderer(this.selections.map(entry => entry.value).join(', '), this.selections.map(entry => entry.label ? entry.label : entry.value).join(', ')) : this._getPlaceholderMulti();
  }

  _getPlaceholderMulti() {
    return this.placeholder || (!this.expanded ? this.label : '');
  }

  _customRenderer(value, label) {
    const userInput = this.searchTerm;
    const displayVal = label ? label : value;
    return this.searchTerm ? this._getFormattedHtml(displayVal, userInput) : html`<span>${displayVal}</span>`;
  }

  _getFormattedHtml(value, userInput) {
    const startIndex = value.toLowerCase().indexOf(userInput.toLowerCase());
    const endIndex = startIndex + userInput.length; // Skip if nothing was found

    if (startIndex < 0) {
      return null;
    }

    const start = value.substring(0, startIndex);
    const bold = value.substring(startIndex, endIndex);
    const end = value.substring(endIndex, value.length);
    return html`
      <span>
        ${start}<strong>${bold}</strong>${end}
      </span>
    `;
  }

  _selectMultiOptionParent(parent) {
    const childElements = parent.querySelectorAll('.option');
    Array.from(childElements).filter(child => !child.disabled).forEach(child => {
      this._selectMultiOption(child, false, !parent.selected);
    });
  }

  _selectMultiOption(option, toggle, selectState) {
    if (toggle) {
      this._toggleOption(option);
    } else {
      if (selectState) {
        this._addSelectionFor(option);
      } else {
        this._removeSelectionFor(option);
      }
    }

    this.resetSelectionCursor();
  }

  _toggleOption(option) {
    if (this._isAlreadySelected(option)) {
      this._removeSelectionFor(option);
    } else {
      this._addSelectionFor(option);
    }
  }

  _addSelectionFor(option) {
    if (option && !this._isAlreadySelected(option)) {
      const foundOption = this.internalDataValuesFlatted.find(value => value.value === option.value);

      this._values.push(foundOption.value);
    }
  }

  _isAlreadySelected(option) {
    return this._values.findIndex(entry => entry === (option === null || option === void 0 ? void 0 : option.value)) > -1;
  }

  _removeSelectionFor(option) {
    this._values = this._values.filter(item => item !== option.value);
  }

  _isOptionChecked(value) {
    return this._values.find(item => item === value) !== undefined;
  }

  _areAllChildrenChecked(children) {
    const enabledChildren = children.filter(child => !(child !== null && child !== void 0 && child.disabled));
    return enabledChildren.length === 0 ? false : enabledChildren.every(child => this._values.find(item => item === (child !== null && child !== void 0 && child.value ? child.value : child)));
  }

  _hasClickableChildren(children) {
    return children.filter(child => !(child !== null && child !== void 0 && child.disabled)).length > 0;
  }

  _getOptionsContainer() {
    return this.shadowRoot.querySelector('.list');
  }

  _getChildrenContainer() {
    return this._selectionCursor.querySelector('.children');
  }

  _hasChildren(option) {
    var _this$_getChildrenCon;

    return option && ((_this$_getChildrenCon = this._getChildrenContainer()) === null || _this$_getChildrenCon === void 0 ? void 0 : _this$_getChildrenCon.children.length) > 0;
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "values", [_dec3, _dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "values"), _class2.prototype), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "changeEvent_", [_dec5, _dec6], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
})), _class2)) || _class) || _class);
